import React, { useEffect, useMemo, useRef, useState } from "react";
import { getCookingSettings, saveCookingSettings } from "../services/cookingService";
import { getCookingDataByLanguage, getRecipeFromCookingData } from "../data/cooking/index.js";
import { readDailyMenuId, writeDailyMenuId } from "../data/cooking/dailyMenu.js";
import {
  DEFAULT_COOKING_FILTERS,
  filterCookingMenus,
  getAvailableAllergens,
  hasActiveCookingFilters,
  readCookingFilters,
  writeCookingFilters,
} from "../data/cooking/menuPersonalization.js";
import {
  getDefaultServingCount,
  ingredientToShoppingItem,
  scaleIngredientText,
} from "../data/cooking/recipeTools.js";
import {
  getCurrentWeekDates,
  getMealPlanDateKey,
  readWeeklyMealPlan,
  writeWeeklyMealPlan,
} from "../data/cooking/weeklyMealPlan.js";
import { useLanguage } from "../i18n/LanguageContext";

/* ─────────────────────────────────────────
   AĞIRLIKLI RANDOM SEÇICI
   - begenmeme puanı yüksek olanlar daha az çıkar
   - son 20 menü tekrar etmez
───────────────────────────────────────── */
function secMenuu(begeniPuanlari, gecmis, menuler = []) {
  const sonYirmi = new Set(gecmis.slice(-20));

  // Aday menüler: son 20'de olmayan
  const adaylar = menuler.filter((m) => !sonYirmi.has(m.id));
  // Hiç aday kalmadıysa (çok az menü durumu) tümünü kullan
  const liste = adaylar.length > 0 ? adaylar : menuler;

  // Ağırlıkları hesapla: begenmeme arttıkça ağırlık düşer
  const agirliklar = liste.map((m) => {
    const puan = begeniPuanlari[m.id] ?? 0; // pozitif=beğeni, negatif=beğenmeme
    // Minimum ağırlık 1, her beğenmeme yarı yarıya azaltır
    const w = Math.max(1, 100 + puan * 15);
    return w;
  });

  const toplam = agirliklar.reduce((a, b) => a + b, 0);
  let r = Math.random() * toplam;
  for (let i = 0; i < liste.length; i++) {
    r -= agirliklar[i];
    if (r <= 0) return liste[i];
  }
  return liste[liste.length - 1];
}

/* ─────────────────────────────────────────
   ANA SAYFA BİLEŞENİ
───────────────────────────────────────── */
export default function NePisirsamPage({
  familyId,
  onGeri,
  isPremium = false,
  premiumConfig = {},
  onPremiumLock,
  onPremiumLimitHit,
  canAddShopping = false,
  onShoppingAdd,
  onOpenShopping,
}) {
  const { t, lang } = useLanguage();
  const cookingData = useMemo(() => getCookingDataByLanguage(lang), [lang]);
  const aktifMenuler = cookingData.menus;
  const [yukleniyor, setYukleniyor]   = useState(true);
  const [menuuYukleniyor, setMenuuYukleniyor] = useState(false);
  const [mevcutMenu, setMevcutMenu]   = useState(null);
  const [begeniPuanlari, setBegeniPuanlari] = useState({});
  const [gecmis, setGecmis]           = useState([]);
  const [begeniDurumu, setBegeniDurumu] = useState(null);
  const [animasyon, setAnimasyon]     = useState(false);
  const [tarifPopup, setTarifPopup]   = useState(null);
  const [gunlukSayac, setGunlukSayac] = useState(0);
  const [gununMenusu, setGununMenusu] = useState(null);
  const [menuKaynagi, setMenuKaynagi] = useState("daily");
  const [filtreler, setFiltreler] = useState({ ...DEFAULT_COOKING_FILTERS });
  const [filtrePaneliAcik, setFiltrePaneliAcik] = useState(false);
  const [haftalikPlanAcik, setHaftalikPlanAcik] = useState(false);
  const [haftalikPlan, setHaftalikPlan] = useState({});
  const [seciliPlanTarihi, setSeciliPlanTarihi] = useState(() => getMealPlanDateKey(new Date()));
  const [porsiyonSayisi, setPorsiyonSayisi] = useState(4);
  const [alisverisSecimiAcik, setAlisverisSecimiAcik] = useState(false);
  const [seciliMalzemeler, setSeciliMalzemeler] = useState([]);
  const [alisveriseEkleniyor, setAlisveriseEkleniyor] = useState(false);
  const [alisverisSonucu, setAlisverisSonucu] = useState(null);
  const pageRef = useRef(null);
  // Sayfa yüklenince en üste scroll
  useEffect(() => {
    window.scrollTo(0, 0);
    if (pageRef.current) pageRef.current.scrollTop = 0;
  }, []);

  useEffect(() => {
    setMevcutMenu(null);
    setBegeniDurumu(null);
    setTarifPopup(null);
    setGecmis([]);
    setBegeniPuanlari({});
    setGununMenusu(null);
    setMenuKaynagi("daily");
    setAnimasyon(false);
    setFiltreler(readCookingFilters({ familyId, lang }));
    setHaftalikPlan(readWeeklyMealPlan({ familyId, lang }));
    setSeciliPlanTarihi(getMealPlanDateKey(new Date()));
    setFiltrePaneliAcik(false);
    setHaftalikPlanAcik(false);
  }, [familyId, lang]);

  /* Backend/PostgreSQL'den yükle */
  useEffect(() => {
    let cancelled = false;
    if (!familyId) return;
    setYukleniyor(true);
    getCookingSettings(familyId, lang)
      .then((settings) => {
        if (cancelled) return;
        const yuklenenPuanlar = settings.begeniPuanlari || {};
        const yuklenenGecmis = Array.isArray(settings.gecmis) ? settings.gecmis : [];
        setBegeniPuanlari(yuklenenPuanlar);
        setGecmis(yuklenenGecmis);

        const kayitliMenuId = readDailyMenuId({ familyId, lang });
        let gunlukMenu = aktifMenuler.find((menu) => Number(menu.id) === kayitliMenuId);

        if (!gunlukMenu) {
          gunlukMenu = secMenuu(yuklenenPuanlar, yuklenenGecmis, aktifMenuler);
          if (gunlukMenu) {
            writeDailyMenuId({ familyId, lang, menuId: gunlukMenu.id });
          }
        }

        setGununMenusu(gunlukMenu || null);
        setMevcutMenu(gunlukMenu || null);
        setMenuKaynagi("daily");
        setAnimasyon(Boolean(gunlukMenu));
      })
      .catch((err) => {
        console.error("NePisirsam backend load error:", err);
        if (cancelled) return;

        const kayitliMenuId = readDailyMenuId({ familyId, lang });
        const kayitliMenu = aktifMenuler.find((menu) => Number(menu.id) === kayitliMenuId);
        const gunlukMenu = kayitliMenu || secMenuu({}, [], aktifMenuler);
        if (!kayitliMenu && gunlukMenu) {
          writeDailyMenuId({ familyId, lang, menuId: gunlukMenu.id });
        }
        setGununMenusu(gunlukMenu || null);
        setMevcutMenu(gunlukMenu || null);
        setMenuKaynagi("daily");
        setAnimasyon(Boolean(gunlukMenu));
      })
      .finally(() => { if (!cancelled) setYukleniyor(false); });
    return () => { cancelled = true; };
  }, [familyId, lang, aktifMenuler]);

  const menuHaritasi = useMemo(
    () => new Map(aktifMenuler.map((menu) => [Number(menu.id), menu])),
    [aktifMenuler],
  );

  const kullanilabilirAlerjenler = useMemo(
    () => getAvailableAllergens(cookingData),
    [cookingData],
  );

  const filtreliMenuler = useMemo(
    () => filterCookingMenus(aktifMenuler, cookingData, filtreler, lang),
    [aktifMenuler, cookingData, filtreler, lang],
  );

  const aktifFiltreVar = useMemo(() => hasActiveCookingFilters(filtreler), [filtreler]);

  const haftaGunleri = useMemo(() => getCurrentWeekDates(new Date()), []);

  const seciliPlanMenusu = menuHaritasi.get(Number(haftalikPlan[seciliPlanTarihi])) || null;

  const tarifTemelPorsiyon = useMemo(
    () => getDefaultServingCount(tarifPopup?.tarif?.porsiyon),
    [tarifPopup],
  );

  const olcekliMalzemeler = useMemo(() => (
    (tarifPopup?.tarif?.malzemeler || []).map((malzeme) => (
      scaleIngredientText(malzeme, porsiyonSayisi, tarifTemelPorsiyon, lang)
    ))
  ), [tarifPopup, porsiyonSayisi, tarifTemelPorsiyon, lang]);

  const begenilenMenuler = useMemo(() => (
    Object.entries(begeniPuanlari)
      .filter(([, puan]) => Number(puan) > 0)
      .sort(([, puanA], [, puanB]) => Number(puanB) - Number(puanA))
      .map(([menuId]) => menuHaritasi.get(Number(menuId)))
      .filter(Boolean)
      .slice(0, 6)
  ), [begeniPuanlari, menuHaritasi]);

  const sonMenuler = useMemo(() => {
    const gorulenler = new Set();
    return [...gecmis]
      .reverse()
      .map((menuId) => Number(menuId))
      .filter((menuId) => {
        if (gorulenler.has(menuId)) return false;
        gorulenler.add(menuId);
        return true;
      })
      .map((menuId) => menuHaritasi.get(menuId))
      .filter(Boolean)
      .slice(0, 6);
  }, [gecmis, menuHaritasi]);

  const bugunEtiketi = useMemo(() => {
    const locale = lang === "en" ? "en-US" : "tr-TR";
    const metin = new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(new Date());
    return metin.charAt(0).toLocaleUpperCase(locale) + metin.slice(1);
  }, [lang]);

  const filtreleriGuncelle = (degisiklik) => {
    const yeniFiltreler = { ...filtreler, ...degisiklik };
    setFiltreler(yeniFiltreler);
    writeCookingFilters({ familyId, lang, filters: yeniFiltreler });
  };

  const filtreleriSifirla = () => {
    const sifirFiltreler = { ...DEFAULT_COOKING_FILTERS };
    setFiltreler(sifirFiltreler);
    writeCookingFilters({ familyId, lang, filters: sifirFiltreler });
  };

  const alerjenDegistir = (alerjen) => {
    const secili = new Set(filtreler.excludedAllergens);
    if (secili.has(alerjen)) secili.delete(alerjen);
    else secili.add(alerjen);
    filtreleriGuncelle({ excludedAllergens: [...secili] });
  };

  /* Backend/PostgreSQL'e kaydet (yardımcı) */
  const kaydet = async (puanlar, yeniGecmis) => {
    if (!familyId) return;
    await saveCookingSettings(familyId, { begeniPuanlari: puanlar, gecmis: yeniGecmis }, lang);
  };

  /* Yeni menü oluştur */
  const yeniMenuOlustur = () => {
    if (filtreliMenuler.length === 0) {
      setFiltrePaneliAcik(true);
      return;
    }
    if (!isPremium) {
      const maxOneri = premiumConfig?.limits?.gunlukNePisirsam ?? 2;
      if (gunlukSayac >= maxOneri) {
        onPremiumLimitHit && onPremiumLimitHit(t("cooking.dailySuggestions"), gunlukSayac, maxOneri, [t("cooking.premium.b1"), t("cooking.premium.b2"), t("cooking.premium.b3"), t("cooking.premium.b4")]);
        return;
      }
    }
    setGunlukSayac(c => c + 1);
    setMenuuYukleniyor(true);
    setAnimasyon(false);
    setBegeniDurumu(null);

    setTimeout(() => {
      const secilen = secMenuu(begeniPuanlari, gecmis, filtreliMenuler);
      const yeniGecmis = [...gecmis, secilen.id].slice(-50); // son 50 tut
      setGecmis(yeniGecmis);
      setMevcutMenu(secilen);
      setMenuKaynagi("suggestion");
      setMenuuYukleniyor(false);
      setAnimasyon(true);
      kaydet(begeniPuanlari, yeniGecmis).catch(console.error);
    }, 600);
  };

  const kayitliMenuAc = (menu, kaynak) => {
    if (!menu) return;
    setAnimasyon(false);
    setBegeniDurumu(null);
    setMevcutMenu(menu);
    setMenuKaynagi(kaynak);
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => setAnimasyon(true));
    } else {
      setAnimasyon(true);
    }
  };

  const gununMenusuneDon = () => kayitliMenuAc(gununMenusu, "daily");

  const haftalikPlaniAcKapat = () => {
    const kilitli = !isPremium && premiumConfig?.lockedFeatures?.haftalikMenu;
    if (kilitli) {
      onPremiumLock && onPremiumLock(
        t("cooking.weeklyPlan"),
        t("cooking.weeklyPlanPremiumDesc"),
        [t("cooking.weeklyBenefit1"), t("cooking.weeklyBenefit2"), t("cooking.weeklyBenefit3"), t("cooking.weeklyBenefit4")],
      );
      return;
    }
    setHaftalikPlanAcik((acik) => !acik);
  };

  const haftalikPlaniKaydet = (yeniPlan) => {
    setHaftalikPlan(yeniPlan);
    writeWeeklyMealPlan({ familyId, lang, plan: yeniPlan });
  };

  const menuPlanla = () => {
    if (!mevcutMenu || !seciliPlanTarihi) return;
    haftalikPlaniKaydet({ ...haftalikPlan, [seciliPlanTarihi]: mevcutMenu.id });
  };

  const planGununuTemizle = () => {
    if (!seciliPlanTarihi) return;
    const yeniPlan = { ...haftalikPlan };
    delete yeniPlan[seciliPlanTarihi];
    haftalikPlaniKaydet(yeniPlan);
  };

  /* Beğeni / beğenmeme */
  const oyVer = async (tip) => {
    if (!mevcutMenu || begeniDurumu) return;
    const fark = tip === "begendi" ? +10 : -10;
    const yeniPuanlar = {
      ...begeniPuanlari,
      [mevcutMenu.id]: (begeniPuanlari[mevcutMenu.id] ?? 0) + fark,
    };
    setBegeniPuanlari(yeniPuanlar);
    setBegeniDurumu(tip);
    await kaydet(yeniPuanlar, gecmis).catch(console.error);
  };
  /* Tarif popup */
  const tarifAc = (ad, tur, ikon) => {
    if (!ad) return;
    const tarif = getRecipeFromCookingData(cookingData, ad);
    const varsayilanPorsiyon = getDefaultServingCount(tarif?.porsiyon);
    setTarifPopup({
      ad,
      tur,
      ikon,
      tarif,
    });
    setPorsiyonSayisi(varsayilanPorsiyon);
    setAlisverisSecimiAcik(false);
    setSeciliMalzemeler((tarif?.malzemeler || []).map((_, index) => index));
    setAlisverisSonucu(null);
  };

  const tarifKapat = () => {
    setTarifPopup(null);
    setAlisverisSecimiAcik(false);
    setAlisverisSonucu(null);
  };

  const malzemeSeciminiDegistir = (index) => {
    setSeciliMalzemeler((mevcut) => (
      mevcut.includes(index)
        ? mevcut.filter((item) => item !== index)
        : [...mevcut, index].sort((a, b) => a - b)
    ));
  };

  const tumMalzemeleriSec = () => setSeciliMalzemeler(olcekliMalzemeler.map((_, index) => index));
  const malzemeSeciminiTemizle = () => setSeciliMalzemeler([]);

  const malzemeleriAlisveriseEkle = async () => {
    if (!onShoppingAdd || seciliMalzemeler.length === 0 || alisveriseEkleniyor) return;
    const urunler = seciliMalzemeler
      .map((index) => ingredientToShoppingItem(olcekliMalzemeler[index], lang))
      .filter(Boolean);
    if (urunler.length === 0) return;
    setAlisveriseEkleniyor(true);
    setAlisverisSonucu(null);
    try {
      const sonuc = await onShoppingAdd(urunler);
      if ((sonuc?.count || 0) > 0) {
        setAlisverisSonucu({ type: "success", count: sonuc.count });
        setAlisverisSecimiAcik(false);
      } else if (sonuc?.error && sonuc.error !== "limit") {
        setAlisverisSonucu({ type: "error", error: sonuc.error });
      }
    } catch (_) {
      setAlisverisSonucu({ type: "error", error: "failed" });
    } finally {
      setAlisveriseEkleniyor(false);
    }
  };

  useEffect(() => {
    if (!tarifPopup) return undefined;
    const oncekiOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = oncekiOverflow;
    };
  }, [tarifPopup]);


  if (yukleniyor) {
    return (
      <div className="nePisirPage" ref={pageRef}>
        <div className="nePisirSkeleton" />
      </div>
    );
  }

  return (
    <div className="nePisirPage">

      {/* BAŞLIK */}
      <div className="nePisirHeader">
        <div className="nePisirHeroGlow nePisirHeroGlowOne" />
        <div className="nePisirHeroGlow nePisirHeroGlowTwo" />
        <div className="nePisirHeaderIcon" aria-hidden="true">🍽️</div>
        <div className="nePisirHeaderCopy">
          <span className="nePisirTodayLabel">{bugunEtiketi}</span>
          <h1 className="nePisirTitle">{t("cooking.title")}</h1>
          <p className="nePisirSub">{t("cooking.sub")}</p>
          <div className="nePisirHeroMeta" aria-label={t("cooking.catalogAria")}> 
            <span><b>500</b> {t("cooking.curatedMenus")}</span>
            <span aria-hidden="true">•</span>
            <span>{t("cooking.cultureMatched")}</span>
          </div>
        </div>
        <span className="nePisirHeroDish nePisirHeroDishOne" aria-hidden="true">🥗</span>
        <span className="nePisirHeroDish nePisirHeroDishTwo" aria-hidden="true">🥣</span>
      </div>

      {/* AKILLI FİLTRELER */}
      <section className={`nePisirFilterCard ${filtrePaneliAcik ? "expanded" : ""}`}>
        <button
          type="button"
          className="nePisirFilterToggle"
          onClick={() => setFiltrePaneliAcik((acik) => !acik)}
          aria-expanded={filtrePaneliAcik}
        >
          <span className="nePisirFilterToggleIcon" aria-hidden="true">⚙️</span>
          <span className="nePisirFilterToggleCopy">
            <strong>{t("cooking.smartFilters")}</strong>
            <small>{aktifFiltreVar ? t("cooking.filtersActive") : t("cooking.filtersHint")}</small>
          </span>
          <span className={`nePisirFilterResult ${filtreliMenuler.length === 0 ? "empty" : ""}`}>
            {filtreliMenuler.length}/{aktifMenuler.length}
          </span>
          <span className="nePisirFilterChevron" aria-hidden="true">⌄</span>
        </button>

        {filtrePaneliAcik && (
          <div className="nePisirFilterBody">
            <div className="nePisirFilterGroup">
              <span className="nePisirFilterLabel">{t("cooking.filter.mainType")}</span>
              <div className="nePisirFilterChips">
                {["all", "meat", "chicken", "fish", "vegetarian"].map((value) => (
                  <button
                    type="button"
                    key={`type-${value}`}
                    className={filtreler.mainType === value ? "active" : ""}
                    onClick={() => filtreleriGuncelle({ mainType: value })}
                  >
                    {t(`cooking.filter.type.${value}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="nePisirFilterGrid">
              <div className="nePisirFilterGroup">
                <span className="nePisirFilterLabel">{t("cooking.filter.maxTime")}</span>
                <div className="nePisirFilterChips compact">
                  {["all", "30", "45", "60"].map((value) => (
                    <button
                      type="button"
                      key={`time-${value}`}
                      className={filtreler.maxTime === value ? "active" : ""}
                      onClick={() => filtreleriGuncelle({ maxTime: value })}
                    >
                      {value === "all" ? t("cooking.filter.all") : t("cooking.filter.minutes", { count: value })}
                    </button>
                  ))}
                </div>
              </div>

              <div className="nePisirFilterGroup">
                <span className="nePisirFilterLabel">{t("cooking.filter.difficulty")}</span>
                <div className="nePisirFilterChips compact">
                  {["all", "easy", "medium"].map((value) => (
                    <button
                      type="button"
                      key={`difficulty-${value}`}
                      className={filtreler.difficulty === value ? "active" : ""}
                      onClick={() => filtreleriGuncelle({ difficulty: value })}
                    >
                      {value === "all" ? t("cooking.filter.all") : t(`cooking.difficulty.${value}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <label className="nePisirSeasonToggle">
              <span>
                <b>🌿 {t("cooking.filter.seasonal")}</b>
                <small>{t("cooking.filter.seasonalHint")}</small>
              </span>
              <input
                type="checkbox"
                checked={filtreler.seasonalOnly}
                onChange={(event) => filtreleriGuncelle({ seasonalOnly: event.target.checked })}
              />
              <i aria-hidden="true" />
            </label>

            <div className="nePisirFilterGroup">
              <span className="nePisirFilterLabel">{t("cooking.filter.allergens")}</span>
              <div className="nePisirFilterChips allergen">
                {kullanilabilirAlerjenler.map((alerjen) => (
                  <button
                    type="button"
                    key={alerjen}
                    className={filtreler.excludedAllergens.includes(alerjen) ? "active danger" : ""}
                    onClick={() => alerjenDegistir(alerjen)}
                  >
                    {filtreler.excludedAllergens.includes(alerjen) ? "✓ " : ""}{alerjen}
                  </button>
                ))}
              </div>
            </div>

            <label className="nePisirExcludeField">
              <span>{t("cooking.filter.excludeIngredients")}</span>
              <input
                value={filtreler.excludedText}
                onChange={(event) => filtreleriGuncelle({ excludedText: event.target.value })}
                placeholder={t("cooking.filter.excludePlaceholder")}
                maxLength={120}
              />
            </label>

            <div className="nePisirFilterFooter">
              <span className={filtreliMenuler.length === 0 ? "error" : ""}>
                {filtreliMenuler.length === 0
                  ? t("cooking.filter.noMatch")
                  : t("cooking.filter.matchCount", { count: filtreliMenuler.length })}
              </span>
              {aktifFiltreVar && (
                <button type="button" onClick={filtreleriSifirla}>{t("cooking.filter.reset")}</button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* MENÜ KARTI veya BOŞLUK */}
      {!mevcutMenu && !menuuYukleniyor ? (
        <div className="nePisirEmptyState">
          <div className="nePisirEmptyOrb" />
          <span className="nePisirEmptyIcon">🥘</span>
          <p className="nePisirEmptyText">{t("cooking.empty").split("\n")[0]}<br />{t("cooking.empty").split("\n")[1]}</p>
        </div>
      ) : menuuYukleniyor ? (
        <div className="nePisirLoadCard">
          <div className="nePisirLoadSpinner" />
          <span className="nePisirLoadText">{t("cooking.loading")}</span>
        </div>
      ) : (
        <div className={`nePisirCard ${animasyon ? "nePisirCardIn" : ""}`}>
          <div className="nePisirCardGlow" />
          <div className="nePisirCardTopline">
            <span className={`nePisirSourceBadge nePisirSource-${menuKaynagi}`}>
              <span aria-hidden="true">{menuKaynagi === "daily" ? "☀️" : menuKaynagi === "liked" ? "💚" : menuKaynagi === "recent" ? "🕘" : menuKaynagi === "planned" ? "📅" : "✨"}</span>
              {t(`cooking.source.${menuKaynagi}`)}
            </span>
            <span className="nePisirCourseCount">{t("cooking.threeCourse")}</span>
          </div>

          {/* ANA YEMEK */}
          <button
            type="button"
            className="nePisirSatir nePisirAna nePisirTarifBtn"
            onClick={() => tarifAc(mevcutMenu.anaYemek, t("cooking.main"), "🍲")}
            aria-label={`${mevcutMenu.anaYemek} ${t("cooking.openRecipe")}`}
          >
            <span className="nePisirSatirIcon">🍲</span>
            <div className="nePisirSatirBilgi">
              <span className="nePisirSatirEtiket">{t("cooking.main")}</span>
              <span className="nePisirSatirDeger">{mevcutMenu.anaYemek}</span>
              <span className="nePisirTarifHint">{t("cooking.tapRecipe")}</span>
            </div>
          </button>

          <div className="nePisirAyrac" />

          {/* ÇORBA */}
          <button
            type="button"
            className="nePisirSatir nePisirTarifBtn"
            onClick={() => tarifAc(mevcutMenu.corba, t("cooking.soup"), "🥣")}
            aria-label={`${mevcutMenu.corba} ${t("cooking.openRecipe")}`}
          >
            <span className="nePisirSatirIcon">🥣</span>
            <div className="nePisirSatirBilgi">
              <span className="nePisirSatirEtiket">{t("cooking.soup")}</span>
              <span className="nePisirSatirDeger">{mevcutMenu.corba}</span>
              <span className="nePisirTarifHint">{t("cooking.tapRecipe")}</span>
            </div>
          </button>

          <div className="nePisirAyrac" />

          {/* SALATA */}
          <button
            type="button"
            className="nePisirSatir nePisirTarifBtn"
            onClick={() => tarifAc(mevcutMenu.salata, t("cooking.salad"), "🥗")}
            aria-label={`${mevcutMenu.salata} ${t("cooking.openRecipe")}`}
          >
            <span className="nePisirSatirIcon">🥗</span>
            <div className="nePisirSatirBilgi">
              <span className="nePisirSatirEtiket">{t("cooking.salad")}</span>
              <span className="nePisirSatirDeger">{mevcutMenu.salata}</span>
              <span className="nePisirTarifHint">{t("cooking.tapRecipe")}</span>
            </div>
          </button>

          {/* BEĞEN / BEĞENME */}
          <div className="nePisirOylar">
            <button
              className={`nePisirOyBtn nePisirBegendi ${begeniDurumu === "begendi" ? "aktif" : ""} ${begeniDurumu && begeniDurumu !== "begendi" ? "soluk" : ""}`}
              onClick={() => oyVer("begendi")}
              disabled={!!begeniDurumu}
              aria-label={t("cooking.like")}
            >
              <span className="nePisirOyIkon">👍</span>
              <span className="nePisirOyLabel">{t("cooking.like")}</span>
            </button>
            <button
              className={`nePisirOyBtn nePisirBegenMedi ${begeniDurumu === "begenMedi" ? "aktif" : ""} ${begeniDurumu && begeniDurumu !== "begenMedi" ? "soluk" : ""}`}
              onClick={() => oyVer("begenMedi")}
              disabled={!!begeniDurumu}
              aria-label={t("cooking.dislike")}
            >
              <span className="nePisirOyIkon">👎</span>
              <span className="nePisirOyLabel">{t("cooking.dislike")}</span>
            </button>
          </div>

          {begeniDurumu && (
            <div className={`nePisirOnay ${begeniDurumu === "begendi" ? "nePisirOnayIyi" : "nePisirOnayKotu"}`}>
              {begeniDurumu === "begendi"
                ? t("cooking.savedLike")
                : t("cooking.savedDislike")}
            </div>
          )}
        </div>
      )}

      {/* OLUŞTUR BUTONU */}
      <button
        className="nePisirBtn"
        onClick={yeniMenuOlustur}
        disabled={menuuYukleniyor || filtreliMenuler.length === 0}
      >
        <span className="nePisirBtnIcon">✨</span>
        {mevcutMenu ? t("cooking.another") : t("cooking.create")}
      </button>

      {gununMenusu && menuKaynagi !== "daily" && (
        <button type="button" className="nePisirDailyReturn" onClick={gununMenusuneDon}>
          <span aria-hidden="true">☀️</span>
          {t("cooking.backToDaily")}
        </button>
      )}

      {/* HAFTALIK PLAN */}
      <section className={`nePisirPlanner ${haftalikPlanAcik ? "expanded" : ""}`}>
        <button
          type="button"
          className="nePisirPlannerToggle"
          onClick={haftalikPlaniAcKapat}
          aria-expanded={haftalikPlanAcik}
        >
          <span className="nePisirPlannerIcon" aria-hidden="true">📅</span>
          <span>
            <strong>{t("cooking.weeklyPlan")}</strong>
            <small>{t("cooking.weeklyPlanHint")}</small>
          </span>
          {!isPremium && premiumConfig?.lockedFeatures?.haftalikMenu && (
            <b className="nePisirPremiumTag">PRO</b>
          )}
          <i aria-hidden="true">⌄</i>
        </button>

        {haftalikPlanAcik && (
          <div className="nePisirPlannerBody">
            <div className="nePisirWeekRail noSwipeBack" data-no-swipe-back="true">
              {haftaGunleri.map((gun) => {
                const tarihKey = getMealPlanDateKey(gun);
                const planMenu = menuHaritasi.get(Number(haftalikPlan[tarihKey])) || null;
                const locale = lang === "en" ? "en-US" : "tr-TR";
                const bugunMu = tarihKey === getMealPlanDateKey(new Date());
                return (
                  <div
                    className={`nePisirPlanDay ${seciliPlanTarihi === tarihKey ? "selected" : ""} ${bugunMu ? "today" : ""}`}
                    key={tarihKey}
                  >
                    <button type="button" className="nePisirPlanDate" onClick={() => setSeciliPlanTarihi(tarihKey)}>
                      <small>{new Intl.DateTimeFormat(locale, { weekday: "short" }).format(gun)}</small>
                      <strong>{gun.getDate()}</strong>
                      <span>{planMenu ? "●" : "○"}</span>
                    </button>
                    {planMenu && (
                      <button
                        type="button"
                        className="nePisirPlanMenu"
                        onClick={() => {
                          setSeciliPlanTarihi(tarihKey);
                          kayitliMenuAc(planMenu, "planned");
                        }}
                        title={planMenu.anaYemek}
                      >
                        {planMenu.anaYemek}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="nePisirPlannerAction">
              <div>
                <span>{t("cooking.selectedDay")}</span>
                <strong>
                  {new Intl.DateTimeFormat(lang === "en" ? "en-US" : "tr-TR", {
                    weekday: "long",
                    day: "numeric",
                    month: "short",
                  }).format(new Date(`${seciliPlanTarihi}T12:00:00`))}
                </strong>
                <small>{seciliPlanMenusu ? seciliPlanMenusu.anaYemek : t("cooking.dayNotPlanned")}</small>
              </div>
              <button type="button" className="nePisirPlanAssign" onClick={menuPlanla} disabled={!mevcutMenu}>
                <span aria-hidden="true">＋</span>{t("cooking.planCurrentMenu")}
              </button>
            </div>

            {seciliPlanMenusu && (
              <button type="button" className="nePisirPlanClear" onClick={planGununuTemizle}>
                {t("cooking.clearPlannedDay")}
              </button>
            )}
          </div>
        )}
      </section>

      {(begenilenMenuler.length > 0 || sonMenuler.length > 0) && (
        <div className="nePisirLibrary">
          {begenilenMenuler.length > 0 && (
            <section className="nePisirShelf" aria-labelledby="nePisirLikedTitle">
              <div className="nePisirShelfHeader">
                <div>
                  <span className="nePisirShelfEyebrow">{t("cooking.yourTaste")}</span>
                  <h2 id="nePisirLikedTitle">{t("cooking.likedMenus")}</h2>
                </div>
                <span className="nePisirShelfCount">{begenilenMenuler.length}</span>
              </div>
              <div className="nePisirQuickList">
                {begenilenMenuler.map((menu) => (
                  <button
                    type="button"
                    className="nePisirQuickCard"
                    key={`liked-${menu.id}`}
                    onClick={() => kayitliMenuAc(menu, "liked")}
                    aria-label={`${menu.anaYemek} · ${t("cooking.openMenu")}`}
                  >
                    <span className="nePisirQuickIcon" aria-hidden="true">💚</span>
                    <span className="nePisirQuickCopy">
                      <strong>{menu.anaYemek}</strong>
                      <small>{menu.corba} · {menu.salata}</small>
                    </span>
                    <span className="nePisirQuickArrow" aria-hidden="true">›</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {sonMenuler.length > 0 && (
            <section className="nePisirShelf" aria-labelledby="nePisirRecentTitle">
              <div className="nePisirShelfHeader">
                <div>
                  <span className="nePisirShelfEyebrow">{t("cooking.pickAgain")}</span>
                  <h2 id="nePisirRecentTitle">{t("cooking.recentMenus")}</h2>
                </div>
                <span className="nePisirShelfCount">{sonMenuler.length}</span>
              </div>
              <div className="nePisirQuickList">
                {sonMenuler.map((menu) => (
                  <button
                    type="button"
                    className="nePisirQuickCard"
                    key={`recent-${menu.id}`}
                    onClick={() => kayitliMenuAc(menu, "recent")}
                    aria-label={`${menu.anaYemek} · ${t("cooking.openMenu")}`}
                  >
                    <span className="nePisirQuickIcon" aria-hidden="true">🕘</span>
                    <span className="nePisirQuickCopy">
                      <strong>{menu.anaYemek}</strong>
                      <small>{menu.corba} · {menu.salata}</small>
                    </span>
                    <span className="nePisirQuickArrow" aria-hidden="true">›</span>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* İSTATİSTİK */}
      {Object.keys(begeniPuanlari).length > 0 && (
        <div className="nePisirStat">
          <span>
            {Object.values(begeniPuanlari).filter((p) => p > 0).length} {t("cooking.liked")} ·{" "}
            {Object.values(begeniPuanlari).filter((p) => p < 0).length} {t("cooking.dislikedMenu")}
          </span>
        </div>
      )}

      {tarifPopup && (
        <div className="nePisirTarifOverlay" onClick={tarifKapat}>
          <div
            className="nePisirTarifModal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="nePisirTarifBaslik"
          >
            <div className="nePisirTarifGlow" />
            <div className="nePisirTarifHeader">
              <div className="nePisirTarifIcon">{tarifPopup.ikon}</div>
              <div className="nePisirTarifTitleWrap">
                <span className="nePisirTarifType">{tarifPopup.tur}</span>
                <h2 id="nePisirTarifBaslik">{tarifPopup.ad}</h2>
                {tarifPopup.tarif && (
                  <p>{tarifPopup.tarif.sure} · {porsiyonSayisi} {t("cooking.servingUnit")}</p>
                )}
              </div>
              <button className="nePisirTarifClose" onClick={tarifKapat} aria-label={t("cooking.closeRecipe")}>×</button>
            </div>

            {tarifPopup.tarif ? (
              <div className="nePisirTarifBody">
                <div className="nePisirTarifMetaGrid">
                  <div className="nePisirTarifMetaItem">
                    <span className="nePisirTarifMetaIcon">🔪</span>
                    <span className="nePisirTarifMetaLabel">{t("cooking.prepTime")}</span>
                    <strong>{tarifPopup.tarif.prepTime || tarifPopup.tarif.sure}</strong>
                  </div>
                  <div className="nePisirTarifMetaItem">
                    <span className="nePisirTarifMetaIcon">🔥</span>
                    <span className="nePisirTarifMetaLabel">{t("cooking.cookTime")}</span>
                    <strong>{tarifPopup.tarif.cookTime || tarifPopup.tarif.sure}</strong>
                  </div>
                  <div className="nePisirTarifMetaItem">
                    <span className="nePisirTarifMetaIcon">⏱️</span>
                    <span className="nePisirTarifMetaLabel">{t("cooking.totalTime")}</span>
                    <strong>{tarifPopup.tarif.totalTime || tarifPopup.tarif.sure}</strong>
                  </div>
                  <div className="nePisirTarifMetaItem">
                    <span className="nePisirTarifMetaIcon">👩‍🍳</span>
                    <span className="nePisirTarifMetaLabel">{t("cooking.difficulty")}</span>
                    <strong>{t(`cooking.difficulty.${tarifPopup.tarif.difficultyKey || "medium"}`)}</strong>
                  </div>
                </div>

                <div className="nePisirServingControl">
                  <div>
                    <span aria-hidden="true">👨‍👩‍👧‍👦</span>
                    <p>
                      <small>{t("cooking.servingSize")}</small>
                      <strong>{porsiyonSayisi} {t("cooking.servingUnit")}</strong>
                    </p>
                  </div>
                  <div className="nePisirServingButtons">
                    <button
                      type="button"
                      onClick={() => setPorsiyonSayisi((sayi) => Math.max(1, sayi - 1))}
                      disabled={porsiyonSayisi <= 1}
                      aria-label={t("cooking.decreaseServing")}
                    >−</button>
                    <b>{porsiyonSayisi}</b>
                    <button
                      type="button"
                      onClick={() => setPorsiyonSayisi((sayi) => Math.min(12, sayi + 1))}
                      disabled={porsiyonSayisi >= 12}
                      aria-label={t("cooking.increaseServing")}
                    >+</button>
                  </div>
                </div>

                <div className="nePisirTarifBlock">
                  <h3>{t("cooking.ingredients")}</h3>
                  <ul>
                    {olcekliMalzemeler.map((malzeme, index) => (
                      <li key={`malzeme-${index}`}>{malzeme}</li>
                    ))}
                  </ul>
                </div>

                {canAddShopping && onShoppingAdd && (
                  <div className="nePisirShoppingTransfer">
                    <button
                      type="button"
                      className="nePisirShoppingOpen"
                      onClick={() => {
                        setAlisverisSecimiAcik((acik) => !acik);
                        setAlisverisSonucu(null);
                      }}
                      aria-expanded={alisverisSecimiAcik}
                    >
                      <span aria-hidden="true">🛒</span>
                      <span>
                        <strong>{t("cooking.addIngredientsShopping")}</strong>
                        <small>{t("cooking.chooseMissingIngredients")}</small>
                      </span>
                      <i aria-hidden="true">⌄</i>
                    </button>

                    {alisverisSecimiAcik && (
                      <div className="nePisirShoppingPicker">
                        <div className="nePisirShoppingPickerTop">
                          <span>{t("cooking.selectedIngredients", { count: seciliMalzemeler.length })}</span>
                          <div>
                            <button type="button" onClick={tumMalzemeleriSec}>{t("cooking.selectAll")}</button>
                            <button type="button" onClick={malzemeSeciminiTemizle}>{t("cooking.clearSelection")}</button>
                          </div>
                        </div>
                        <div className="nePisirShoppingIngredientList">
                          {olcekliMalzemeler.map((malzeme, index) => (
                            <label key={`shopping-${index}`} className={seciliMalzemeler.includes(index) ? "selected" : ""}>
                              <input
                                type="checkbox"
                                checked={seciliMalzemeler.includes(index)}
                                onChange={() => malzemeSeciminiDegistir(index)}
                              />
                              <span aria-hidden="true">✓</span>
                              <b>{malzeme}</b>
                            </label>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="nePisirShoppingConfirm"
                          onClick={malzemeleriAlisveriseEkle}
                          disabled={seciliMalzemeler.length === 0 || alisveriseEkleniyor}
                        >
                          {alisveriseEkleniyor
                            ? t("cooking.addingShopping")
                            : t("cooking.addSelectedShopping", { count: seciliMalzemeler.length })}
                        </button>
                      </div>
                    )}

                    {alisverisSonucu?.type === "success" && (
                      <div className="nePisirShoppingSuccess" role="status">
                        <span>✓ {t("cooking.shoppingAdded", { count: alisverisSonucu.count })}</span>
                        {onOpenShopping && (
                          <button type="button" onClick={onOpenShopping}>{t("cooking.openShoppingList")}</button>
                        )}
                      </div>
                    )}
                    {alisverisSonucu?.type === "error" && (
                      <div className="nePisirShoppingError" role="alert">{t("cooking.shoppingAddFailed")}</div>
                    )}
                  </div>
                )}

                <div className="nePisirTarifBlock">
                  <h3>{t("cooking.steps")}</h3>
                  <ol>
                    {tarifPopup.tarif.yapilis.map((adim, index) => (
                      <li key={`adim-${index}`}>{adim}</li>
                    ))}
                  </ol>
                </div>

                {Array.isArray(tarifPopup.tarif.tips) && tarifPopup.tarif.tips.length > 0 && (
                  <div className="nePisirTarifBlock nePisirTarifAccentBlock">
                    <h3>💡 {t("cooking.tips")}</h3>
                    <ul>
                      {tarifPopup.tarif.tips.map((ipucu, index) => (
                        <li key={`ipucu-${index}`}>{ipucu}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(tarifPopup.tarif.substitutions) && tarifPopup.tarif.substitutions.length > 0 && (
                  <div className="nePisirTarifBlock">
                    <h3>🔄 {t("cooking.substitutions")}</h3>
                    <ul>
                      {tarifPopup.tarif.substitutions.map((alternatif, index) => (
                        <li key={`alternatif-${index}`}>{alternatif}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="nePisirTarifInfoGrid">
                  {tarifPopup.tarif.safetyNote && (
                    <div className="nePisirTarifInfoCard nePisirTarifSafetyCard">
                      <span className="nePisirTarifInfoIcon">🌡️</span>
                      <div>
                        <strong>{t("cooking.foodSafety")}</strong>
                        <p>{tarifPopup.tarif.safetyNote}</p>
                      </div>
                    </div>
                  )}
                  {tarifPopup.tarif.storage && (
                    <div className="nePisirTarifInfoCard">
                      <span className="nePisirTarifInfoIcon">❄️</span>
                      <div>
                        <strong>{t("cooking.storage")}</strong>
                        <p>{tarifPopup.tarif.storage}</p>
                      </div>
                    </div>
                  )}
                  {Array.isArray(tarifPopup.tarif.allergens) && (
                    <div className="nePisirTarifInfoCard">
                      <span className="nePisirTarifInfoIcon">⚠️</span>
                      <div>
                        <strong>{t("cooking.allergens")}</strong>
                        <p>{tarifPopup.tarif.allergens.length > 0
                          ? tarifPopup.tarif.allergens.join(" · ")
                          : t("cooking.noDeclaredAllergens")}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="nePisirTarifEmpty">{t("cooking.recipeMissing")}</div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .nePisirTarifBtn {
          width: 100%;
          border: none;
          text-align: left;
          cursor: pointer;
          color: inherit;
          font: inherit;
        }

        .nePisirTarifBtn:active {
          transform: scale(0.99);
        }

        .nePisirTarifHint {
          display: block;
          margin-top: 4px;
          font-size: 11px;
          font-weight: 800;
          color: #a78bfa;
          opacity: 0.9;
        }

        .nePisirTarifOverlay {
          position: fixed;
          inset: 0;
          z-index: 10020;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 16px 16px max(16px, env(safe-area-inset-bottom));
          background: rgba(0, 0, 0, 0.62);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          animation: nePisirTarifFade 0.18s ease;
        }

        .nePisirTarifModal {
          position: relative;
          width: 100%;
          max-width: 480px;
          max-height: min(86dvh, 780px);
          overflow: hidden;
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 26px;
          background: linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(30, 41, 59, 0.96));
          color: var(--text-primary);
          box-shadow: 0 -18px 70px rgba(0, 0, 0, 0.48);
          animation: nePisirTarifSlide 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .nePisirTarifGlow {
          position: absolute;
          top: -80px;
          right: -60px;
          width: 190px;
          height: 190px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.34), transparent 68%);
          pointer-events: none;
        }

        .nePisirTarifHeader {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 18px 14px;
          border-bottom: 1px solid rgba(148, 163, 184, 0.16);
        }

        .nePisirTarifIcon {
          width: 48px;
          height: 48px;
          flex-shrink: 0;
          display: grid;
          place-items: center;
          border-radius: 18px;
          background: rgba(99, 102, 241, 0.16);
          border: 1px solid rgba(129, 140, 248, 0.24);
          font-size: 24px;
        }

        .nePisirTarifTitleWrap {
          flex: 1;
          min-width: 0;
        }

        .nePisirTarifType {
          display: block;
          margin-bottom: 3px;
          color: #a78bfa;
          font-size: 11px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.6px;
        }

        .nePisirTarifTitleWrap h2 {
          margin: 0;
          color: var(--text-primary);
          font-size: 20px;
          line-height: 1.18;
          font-weight: 950;
          letter-spacing: -0.3px;
        }

        .nePisirTarifTitleWrap p {
          margin: 5px 0 0;
          color: var(--text-secondary);
          font-size: 12px;
          font-weight: 700;
        }

        .nePisirTarifClose {
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          border: 1px solid rgba(148, 163, 184, 0.22);
          border-radius: 13px;
          background: rgba(255, 255, 255, 0.06);
          color: var(--text-secondary);
          font-size: 22px;
          font-weight: 900;
          cursor: pointer;
        }

        .nePisirTarifBody {
          position: relative;
          z-index: 1;
          max-height: calc(min(86dvh, 780px) - 96px);
          overflow-y: auto;
          padding: 16px 18px 20px;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: rgba(129, 140, 248, 0.35) transparent;
        }

        .nePisirTarifBlock {
          padding: 14px;
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.045);
          border: 1px solid rgba(148, 163, 184, 0.16);
          margin-bottom: 12px;
        }

        .nePisirTarifMetaGrid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 9px;
          margin-bottom: 12px;
        }

        .nePisirTarifMetaItem {
          min-width: 0;
          display: grid;
          grid-template-columns: 28px minmax(0, 1fr);
          grid-template-areas:
            "icon label"
            "icon value";
          column-gap: 8px;
          align-items: center;
          padding: 10px;
          border-radius: 15px;
          border: 1px solid rgba(129, 140, 248, 0.18);
          background: rgba(99, 102, 241, 0.08);
        }

        .nePisirTarifMetaIcon {
          grid-area: icon;
          width: 28px;
          height: 28px;
          display: grid;
          place-items: center;
          border-radius: 10px;
          background: rgba(129, 140, 248, 0.13);
          font-size: 14px;
        }

        .nePisirTarifMetaLabel {
          grid-area: label;
          overflow: hidden;
          color: var(--text-secondary);
          font-size: 10px;
          font-weight: 800;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .nePisirTarifMetaItem strong {
          grid-area: value;
          overflow: hidden;
          color: var(--text-primary);
          font-size: 12px;
          font-weight: 950;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .nePisirTarifBlock h3 {
          margin: 0 0 10px;
          color: var(--text-primary);
          font-size: 14px;
          font-weight: 950;
        }

        .nePisirTarifBlock ul,
        .nePisirTarifBlock ol {
          margin: 0;
          padding-left: 20px;
          color: var(--text-secondary);
          font-size: 13px;
          line-height: 1.55;
          font-weight: 650;
        }

        .nePisirTarifBlock li {
          margin-bottom: 7px;
        }

        .nePisirTarifAccentBlock {
          border-color: rgba(245, 158, 11, 0.25);
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(249, 115, 22, 0.04));
        }

        .nePisirTarifInfoGrid {
          display: grid;
          gap: 9px;
        }

        .nePisirTarifInfoCard {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px;
          border: 1px solid rgba(148, 163, 184, 0.16);
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.035);
        }

        .nePisirTarifSafetyCard {
          border-color: rgba(16, 185, 129, 0.25);
          background: rgba(16, 185, 129, 0.07);
        }

        .nePisirTarifInfoIcon {
          width: 30px;
          height: 30px;
          flex: 0 0 30px;
          display: grid;
          place-items: center;
          border-radius: 10px;
          background: rgba(129, 140, 248, 0.12);
          font-size: 15px;
        }

        .nePisirTarifInfoCard strong {
          display: block;
          margin-bottom: 4px;
          color: var(--text-primary);
          font-size: 12px;
          font-weight: 950;
        }

        .nePisirTarifInfoCard p {
          margin: 0;
          color: var(--text-secondary);
          font-size: 11px;
          font-weight: 650;
          line-height: 1.5;
        }

        .nePisirTarifEmpty {
          padding: 20px;
          color: var(--text-secondary);
          font-weight: 700;
        }

        body[data-tema="light"] .nePisirTarifModal {
          background: linear-gradient(180deg, #ffffff, #f6f8ff);
          border-color: rgba(99, 102, 241, 0.18);
          box-shadow: 0 -16px 60px rgba(99, 102, 241, 0.18);
        }

        body[data-tema="light"] .nePisirTarifBlock {
          background: #ffffff;
          border-color: rgba(99, 102, 241, 0.14);
        }

        body[data-tema="light"] .nePisirTarifMetaItem {
          background: #f7f7ff;
          border-color: rgba(99, 102, 241, 0.14);
        }

        body[data-tema="light"] .nePisirTarifInfoCard {
          background: #ffffff;
          border-color: rgba(99, 102, 241, 0.12);
        }

        body[data-tema="light"] .nePisirTarifSafetyCard {
          background: #f0fdf8;
          border-color: rgba(16, 185, 129, 0.2);
        }

        @media (max-width: 360px) {
          .nePisirTarifOverlay {
            padding-left: 10px;
            padding-right: 10px;
          }

          .nePisirTarifBody {
            padding-left: 12px;
            padding-right: 12px;
          }

          .nePisirTarifMetaItem {
            padding: 9px 8px;
          }
        }

        body[data-tema="light"] .nePisirTarifClose {
          background: #f1f5f9;
          border-color: rgba(99, 102, 241, 0.18);
          color: #475569;
        }

        @keyframes nePisirTarifFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes nePisirTarifSlide {
          from { transform: translateY(24px) scale(0.98); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}</style>

    </div>
  );
}
